import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import styled from "styled-components";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { Container } from "../../components/Container";
import { Card } from "../../components/Card";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { SelectInput } from "../../components/SelectInput";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { PeriodBarChart } from "./PeriodBarChart";
import { StatsDetailContent } from "./StatsDetailContent";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type { StatsPeriod } from "../../api/statsApi";
import type { TFunction } from "i18next";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { useStatsScope } from "../../hooks/useStatsScope";
import {
  useGetStatsQuery,
  useRefreshChainStatsMutation,
} from "../../query/statsQuery";
import { formatDateTime } from "../../utils/dateUtils";

const PERIOD_OPTIONS: { key: StatsPeriod }[] = [
  { key: "days" },
  { key: "weeks" },
  { key: "months" },
];

const DEFAULT_SPAN: Record<StatsPeriod, number> = {
  days: 7,
  weeks: 8,
  months: 6,
};

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
`;

const Title = styled.h1`
  font-size: ${fontSizes.xlarge}px;
  font-weight: 700;
  color: ${colors.text};
  margin: 0;
`;

const RefreshTooltip = styled.div`
  position: absolute;
  left: 50%;
  top: calc(100% + ${spacing.x1}px);
  transform: translateX(-50%);
  width: max-content;
  max-width: 240px;
  padding: ${spacing.x1}px ${spacing.x2}px;
  background-color: ${colors.text};
  color: ${colors.background};
  font-size: ${fontSizes.small}px;
  line-height: 1.4;
  border-radius: ${borderRadius.small}px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s, visibility 0.15s;
  z-index: 20;
  pointer-events: none;
`;

const RefreshWrap = styled.div`
  position: relative;
  display: flex;

  &:hover > ${RefreshTooltip} {
    opacity: 1;
    visibility: visible;
  }
`;

const LastUpdated = styled.p`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
  margin: ${spacing.x1}px 0 0 0;
  text-align: right;
`;

const RefreshIcon = styled(RefreshCw)<{ $spinning?: boolean }>`
  animation: ${({ $spinning }) => ($spinning ? "spin 0.8s linear infinite" : "none")};

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${spacing.x2}px;
  flex-wrap: wrap;
  gap: ${spacing.x1}px;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.x2}px;
`;

const PeriodSelect = styled(SelectInput)`
  width: auto;
  min-width: 120px;
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.x1}px;
  color: ${colors.text};
  background: transparent;
  border: none;
  border-radius: ${borderRadius.regular}px;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;

  &:hover:not(:disabled) {
    color: ${colors.primary};
    background: ${colors.backgroundDark};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PeriodLabel = styled.button`
  padding: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
  background: transparent;
  background-color: ${colors.background};
  border: none;
  border-radius: ${borderRadius.regular}px;
  cursor: pointer;
  text-align: center;

  &:hover {
    background: ${colors.backgroundLight};
  }
`;

const ChartCard = styled(Card)`
  padding: ${spacing.x2}px;
  margin-bottom: ${spacing.x2}px;
`;

const alignToPeriod = (dt: DateTime, period: StatsPeriod): DateTime => {
  if (period === "days") return dt.startOf("day");
  if (period === "weeks") return dt.startOf("week");
  return dt.startOf("month");
};

const getFromTo = (
  period: StatsPeriod,
  start: DateTime,
  end: DateTime,
  t: TFunction
): { from: string; to: string; periodLabel: string } => {
  const alignedStart = alignToPeriod(start, period);
  const alignedEnd =
    period === "days"
      ? end.endOf("day")
      : period === "weeks"
        ? end.endOf("week")
        : end.endOf("month");

  if (period === "days") {
    return {
      from: alignedStart.toFormat("yyyy-MM-dd"),
      to: alignedEnd.toFormat("yyyy-MM-dd"),
      periodLabel: `${alignedStart.toFormat("d MMM")} – ${alignedEnd.toFormat("d MMM yyyy")}`,
    };
  }
  if (period === "weeks") {
    const formatWeek = (dt: DateTime) =>
      `${dt.weekYear}-W${String(dt.weekNumber).padStart(2, "0")}`;
    const fromWeek = formatWeek(alignedStart);
    const toWeek = formatWeek(alignedEnd);
    const fromNum = alignedStart.weekNumber;
    const toNum = alignedEnd.weekNumber;
    const sameYear = alignedStart.year === alignedEnd.year;
    const periodLabel = sameYear
      ? `${t("statistics.weekLabel", { number: fromNum })} – ${t(
          "statistics.weekLabel",
          { number: toNum }
        )}`
      : `${t("statistics.weekLabelWithYear", {
          number: fromNum,
          year: alignedStart.year,
        })} – ${t("statistics.weekLabelWithYear", {
          number: toNum,
          year: alignedEnd.year,
        })}`;
    return { from: fromWeek, to: toWeek, periodLabel };
  }
  return {
    from: alignedStart.toFormat("yyyy-MM"),
    to: alignedEnd.toFormat("yyyy-MM"),
    periodLabel: `${alignedStart.toFormat("MMM yyyy")} – ${alignedEnd.toFormat("MMM yyyy")}`,
  };
};

const parsePeriodEnd = (to: string, period: StatsPeriod): DateTime => {
  if (period === "days") {
    return DateTime.fromISO(to).endOf("day");
  }
  if (period === "weeks") {
    const parsed = DateTime.fromISO(`${to}-7`);
    if (!parsed.isValid) return DateTime.now().endOf("day");
    return parsed.endOf("day");
  }
  return DateTime.fromFormat(to, "yyyy-MM").endOf("month");
};

const parsePeriodStart = (from: string, period: StatsPeriod): DateTime => {
  if (period === "days") {
    return DateTime.fromISO(from).startOf("day");
  }
  if (period === "weeks") {
    const parsed = DateTime.fromISO(`${from}-1`);
    if (!parsed.isValid) return DateTime.now().startOf("week");
    return parsed.startOf("week");
  }
  return DateTime.fromFormat(from, "yyyy-MM").startOf("month");
};

const CHAIN_REFRESH_COOLDOWN_MS = 30 * 60 * 1000;

const currentPeriodId = (period: StatsPeriod, now: DateTime): string => {
  if (period === "days") return now.toFormat("yyyy-MM-dd");
  if (period === "weeks") {
    return `${now.weekYear}-W${String(now.weekNumber).padStart(2, "0")}`;
  }
  return now.toFormat("yyyy-MM");
};

export const StatsView = () => {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const scope = useStatsScope();
  const [searchParams, setSearchParams] = useSearchParams();

  const today = DateTime.now().endOf("day");
  const urlFrom = searchParams.get("from");
  const urlTo = searchParams.get("to");
  const urlPeriod = searchParams.get("period") as StatsPeriod | null;
  const urlSelected = searchParams.get("selected");

  const getInitialPeriodEnd = () => {
    if (urlTo && urlPeriod && ["days", "weeks", "months"].includes(urlPeriod)) {
      const parsed = parsePeriodEnd(urlTo, urlPeriod);
      return parsed > today ? today : parsed;
    }
    return today;
  };

  const getInitialPeriod = (): StatsPeriod =>
    urlPeriod && ["days", "weeks", "months"].includes(urlPeriod)
      ? urlPeriod
      : "days";

  const getInitialCustomRange = (): {
    startDate: DateTime;
    endDate: DateTime;
  } | null => {
    if (
      !urlFrom ||
      !urlTo ||
      !urlPeriod ||
      !["days", "weeks", "months"].includes(urlPeriod)
    ) {
      return null;
    }
    const start = parsePeriodStart(urlFrom, urlPeriod);
    const end = parsePeriodEnd(urlTo, urlPeriod);
    if (!start.isValid || !end.isValid || start > end || end > today) {
      return null;
    }
    const clampedEnd = end > today ? today : end;
    return { startDate: start, endDate: clampedEnd };
  };

  const [periodEnd, setPeriodEnd] = useState<DateTime>(getInitialPeriodEnd);
  const [period, setPeriod] = useState<StatsPeriod>(getInitialPeriod);
  const [selectedDate, setSelectedDate] = useState<string | null>(
    () => urlSelected
  );
  const [customRange, setCustomRange] = useState<{
    startDate: DateTime;
    endDate: DateTime;
  } | null>(getInitialCustomRange);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [pickerStart, setPickerStart] = useState("");
  const [pickerEnd, setPickerEnd] = useState("");
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const interval = window.setInterval(() => setNowMs(Date.now()), 60_000);
    return () => window.clearInterval(interval);
  }, []);

  const clampedPeriodEnd =
    periodEnd > today ? today : periodEnd;
  const span = DEFAULT_SPAN[period];
  const presetStart =
    period === "days"
      ? clampedPeriodEnd.minus({ days: span - 1 }).startOf("day")
      : period === "weeks"
        ? clampedPeriodEnd.minus({ weeks: span - 1 }).startOf("week")
        : clampedPeriodEnd.minus({ months: span - 1 }).startOf("month");

  const { from, to, periodLabel } = (() => {
    if (customRange) {
      return getFromTo(period, customRange.startDate, customRange.endDate, t);
    }
    return getFromTo(period, presetStart, clampedPeriodEnd, t);
  })();

  const urlStore = searchParams.get("store");

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("from", from);
    params.set("to", to);
    params.set("period", period);
    if (selectedDate) {
      params.set("selected", selectedDate);
    } else {
      params.delete("selected");
    }
    if (urlStore) {
      params.set("store", urlStore);
    }
    setSearchParams(params, { replace: true });
  }, [from, to, period, selectedDate, urlStore, setSearchParams]);

  const getShiftAmount = () => {
    if (!customRange) return span;
    if (period === "days") {
      return Math.floor(
        customRange.endDate.diff(customRange.startDate, "days").days
      ) + 1;
    }
    if (period === "weeks") {
      return Math.ceil(
        customRange.endDate.diff(customRange.startDate, "weeks").weeks
      ) || 1;
    }
    return (
      (customRange.endDate.year - customRange.startDate.year) * 12 +
      (customRange.endDate.month - customRange.startDate.month) + 1
    ) || 1;
  };

  const handlePrevPeriod = () => {
    if (customRange) {
      const shift = getShiftAmount();
      if (period === "days") {
        setCustomRange({
          startDate: customRange.startDate.minus({ days: shift }),
          endDate: customRange.endDate.minus({ days: shift }),
        });
      } else if (period === "weeks") {
        setCustomRange({
          startDate: customRange.startDate.minus({ weeks: shift }),
          endDate: customRange.endDate.minus({ weeks: shift }),
        });
      } else {
        setCustomRange({
          startDate: customRange.startDate.minus({ months: shift }),
          endDate: customRange.endDate.minus({ months: shift }),
        });
      }
    } else {
      setPeriodEnd((prev) => {
        if (period === "weeks") return prev.minus({ weeks: span });
        if (period === "months") return prev.minus({ months: span });
        return prev.minus({ days: span });
      });
    }
  };

  const handleNextPeriod = () => {
    if (customRange) {
      const shift = getShiftAmount();
      let newEnd: DateTime;
      if (period === "days") {
        newEnd = customRange.endDate.plus({ days: shift });
      } else if (period === "weeks") {
        newEnd = customRange.endDate.plus({ weeks: shift });
      } else {
        newEnd = customRange.endDate.plus({ months: shift });
      }
      const clampedEnd = newEnd > today ? today : newEnd;
      let newStart: DateTime;
      if (period === "days") {
        newStart = clampedEnd.minus({ days: shift - 1 }).startOf("day");
      } else if (period === "weeks") {
        newStart = clampedEnd.minus({ weeks: shift - 1 }).startOf("week");
      } else {
        newStart = clampedEnd.minus({ months: shift - 1 }).startOf("month");
      }
      setCustomRange({
        startDate: newStart,
        endDate: clampedEnd,
      });
    } else {
      setPeriodEnd((prev) => {
        let next: DateTime;
        if (period === "weeks") next = prev.plus({ weeks: span });
        else if (period === "months") next = prev.plus({ months: span });
        else next = prev.plus({ days: span });
        return next > today ? today : next;
      });
    }
  };

  const dateRangeEnd = customRange ? customRange.endDate : clampedPeriodEnd;
  const canGoNext = dateRangeEnd < today;

  const handleSelectDay = (date: string) => {
    setSelectedDate((prev) => (prev === date ? null : date));
  };

  const pickerRangeStart = customRange ? customRange.startDate : presetStart;
  const pickerRangeEnd = customRange ? customRange.endDate : clampedPeriodEnd;

  const handleOpenDatePicker = () => {
    setPickerStart(pickerRangeStart.toFormat("yyyy-MM-dd"));
    setPickerEnd(pickerRangeEnd.toFormat("yyyy-MM-dd"));
    setIsDatePickerOpen(true);
  };

  const handleApplyCustomRange = () => {
    const start = DateTime.fromISO(pickerStart).startOf("day");
    const end = DateTime.fromISO(pickerEnd).endOf("day");
    if (start.isValid && end.isValid && start <= end) {
      const clampedEnd = end > today ? today : end;
      setSelectedDate(null);
      setCustomRange({
        startDate: start,
        endDate: clampedEnd,
      });
      setIsDatePickerOpen(false);
    }
  };

  const { data: rangeStats } = useGetStatsQuery(scope ?? undefined, period, from, to);
  const currentId = currentPeriodId(period, DateTime.now());
  const currentPeriodStats = rangeStats?.find((s) => s.id === currentId);
  const minutesUntilRefresh = currentPeriodStats?.computedAt
    ? Math.max(
        1,
        30 -
          Math.floor(
            (nowMs -
              DateTime.fromISO(currentPeriodStats.computedAt).toMillis()) /
              60000
          )
      )
    : 30;
  const refreshOnCooldown =
    !isAdmin &&
    currentPeriodStats?.computedAt != null &&
    nowMs - DateTime.fromISO(currentPeriodStats.computedAt).toMillis() <
      CHAIN_REFRESH_COOLDOWN_MS;
  const { mutate: refreshChainStats, isPending: isRefreshing } =
    useRefreshChainStatsMutation(scope?.type === "chain" ? scope.id : undefined);

  if (!scope) {
    return null;
  }

  return (
    <Container>
      <Header>
        <TitleRow>
          <Title>{t("statistics.title")}</Title>
          {scope.type === "chain" && (
            <RefreshWrap>
              <NavButton
                onClick={() => refreshChainStats()}
                disabled={isRefreshing || refreshOnCooldown}
                aria-label={t("statistics.refresh")}
              >
                <RefreshIcon size={16} $spinning={isRefreshing} />
              </NavButton>
              {refreshOnCooldown && (
                <RefreshTooltip>
                  {t("statistics.refreshDisabledCooldown", {
                    minutes: minutesUntilRefresh,
                  })}
                </RefreshTooltip>
              )}
            </RefreshWrap>
          )}
        </TitleRow>
        <Controls>
          <NavRow>
            <NavButton onClick={handlePrevPeriod} aria-label={t("statistics.previousPeriod")}>
              <ChevronLeft size={20} />
            </NavButton>
            <PeriodLabel onClick={handleOpenDatePicker}>{periodLabel}</PeriodLabel>
            <NavButton
              onClick={handleNextPeriod}
              disabled={!canGoNext}
              aria-label={t("statistics.nextPeriod")}
            >
              <ChevronRight size={20} />
            </NavButton>
          </NavRow>
          <PeriodSelect
            value={period}
            onChange={(e) => {
              setSelectedDate(null);
              setCustomRange(null);
              setPeriodEnd(today);
              setPeriod(e.target.value as StatsPeriod);
            }}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {t(`statistics.${opt.key}`)}
              </option>
            ))}
          </PeriodSelect>
        </Controls>
      </Header>
      <ChartCard>
        <h3 style={{ margin: `0 0 ${spacing.x2}px 0` }}>
          {t("statistics.totalSalesAmount")}
        </h3>
        <PeriodBarChart
          scope={scope}
          from={from}
          to={to}
          period={period}
          onSelect={handleSelectDay}
          selectedItem={selectedDate ?? undefined}
        />
        {scope.type === "chain" && currentPeriodStats?.computedAt && (
          <LastUpdated>
            {t("statistics.lastUpdated", {
              time: formatDateTime(currentPeriodStats.computedAt),
            })}
          </LastUpdated>
        )}
      </ChartCard>
      <Modal
        isOpen={isDatePickerOpen}
        onClose={() => setIsDatePickerOpen(false)}
        title={t("statistics.selectPeriod")}
        maxWidth={400}
      >
        <FormGroup style={{ marginBottom: spacing.x2 }}>
          <FormLabel>{t("statistics.startDate")}</FormLabel>
          <input
            type="date"
            value={pickerStart}
            onChange={(e) => setPickerStart(e.target.value)}
            max={today.toFormat("yyyy-MM-dd")}
            style={{
              padding: `${spacing.x1}px ${spacing.x2}px`,
              border: `1px solid ${colors.border}`,
              borderRadius: 4,
              fontSize: fontSizes.regular,
            }}
          />
        </FormGroup>
        <FormGroup style={{ marginBottom: spacing.x2 }}>
          <FormLabel>{t("statistics.endDate")}</FormLabel>
          <input
            type="date"
            value={pickerEnd}
            onChange={(e) => setPickerEnd(e.target.value)}
            max={today.toFormat("yyyy-MM-dd")}
            style={{
              padding: `${spacing.x1}px ${spacing.x2}px`,
              border: `1px solid ${colors.border}`,
              borderRadius: 4,
              fontSize: fontSizes.regular,
            }}
          />
        </FormGroup>
        <Button onClick={handleApplyCustomRange}>{t("statistics.apply")}</Button>
      </Modal>
      {selectedDate && (
        <StatsDetailContent
          scope={scope}
          period={period}
          selectedId={selectedDate}
          from={from}
          to={to}
        />
      )}
    </Container>
  );
};
